/**
 * DischargeModal Component
 * Modal for discharging powers — supports both individual and bulk modes.
 * Both modes use the same pipeline: validatePowers → form → confirm → submitDischarge
 * (same as the Discharges page, without the queue).
 *
 * - Individual mode: pass `power` prop
 * - Bulk mode: pass `selectedPowers` (Set), `selectAllMatching`, `totalMatchingCount`
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathInput, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import { validatePowers, submitDischarge } from '../../../../services/oathDischargeService';
import { categorizeValidations, mapValidationToDisplay } from '../../discharges/utils/dischargeValidation';
import { formatCurrencyWithSymbol } from '../../discharges/utils/formatters';
import ProofUploadModal from '../../discharges/modals/ProofUploadModal';

// ============================================================================
// Sub-components for the validated power list (bulk mode form step)
// ============================================================================

const PowerRow = ({ power, variant = 'ready', proofCount = 0, onUploadProof, onRemove }) => {
  const hasProof = proofCount > 0;
  const isDischargeable = variant === 'ready' || variant === 'needsProof';

  // Match DischargeCart color coding: green (has proof), orange (needs proof), gray (optional)
  const noProofClasses = variant === 'needsProof'
    ? 'bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400 dark:hover:bg-orange-900/30'
    : 'bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-gray-700/50 dark:text-gray-400 dark:hover:bg-gray-700';

  return (
    <div className="px-3 py-2 space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="font-mono font-bold text-primary dark:text-primary-light text-sm whitespace-nowrap">
            {power.powerNo}
          </span>
          <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
            {power.defendant || '—'}
          </span>
        </div>
        <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">
          {power.liability != null ? formatCurrencyWithSymbol(power.liability) : '—'}
        </span>
        {onRemove && (
          <button
            onClick={() => onRemove(power.powerNo)}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            title="Remove power"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      {variant === 'needsProof' && power.reason && (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
          {power.reason}
        </span>
      )}
      {variant === 'invalid' && power.validationErrors?.length > 0 && (
        <p className="text-xs font-medium text-red-600 dark:text-red-400 pl-0.5">
          {power.validationErrors[0]}
        </p>
      )}
      {isDischargeable && onUploadProof && (
        <button
          onClick={() => onUploadProof(power)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            hasProof
              ? 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30'
              : noProofClasses
          }`}
          title={hasProof ? 'Manage uploaded proof' : variant === 'needsProof' ? 'Upload proof' : 'Upload proof (optional)'}
        >
          {hasProof ? (
            <>
              <CheckCircleIcon className="h-4 w-4" />
              <span>Proof Uploaded</span>
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-300 text-xs font-bold">
                {proofCount}
              </span>
            </>
          ) : (
            <>
              <ArrowUpTrayIcon className="h-4 w-4" />
              <span>Upload</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

const SectionBlock = ({ title, powers, color, variant, proofFilesByPower, onUploadProof, onRemove }) => {
  const [collapsed, setCollapsed] = useState(() => powers.length > 10);

  if (powers.length === 0) return null;

  const colorMap = {
    green: {
      gradient: 'from-green-400 via-green-500 to-green-400',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      icon: 'text-green-600 dark:text-green-400',
    },
    orange: {
      gradient: 'from-orange-400 via-orange-500 to-orange-400',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      border: 'border-orange-200 dark:border-orange-800',
      badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      icon: 'text-orange-600 dark:text-orange-400',
    },
    red: {
      gradient: 'from-red-400 via-red-500 to-red-400',
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      icon: 'text-red-600 dark:text-red-400',
    },
  };

  const c = colorMap[color];

  return (
    <div className={`rounded-xl border ${c.border} overflow-hidden shadow-sm`}>
      {/* Accent bar */}
      <div className={`h-1 bg-gradient-to-r ${c.gradient}`} />
      {/* Clickable header */}
      <button
        type="button"
        onClick={() => setCollapsed((prev) => !prev)}
        className={`w-full px-3 py-2.5 ${c.bg} flex items-center justify-between hover:brightness-95 dark:hover:brightness-110 transition-all`}
      >
        <div className="flex items-center gap-2">
          {color === 'green' && <CheckCircleIcon className={`h-4 w-4 ${c.icon}`} />}
          {color === 'orange' && <ExclamationCircleIcon className={`h-4 w-4 ${c.icon}`} />}
          {color === 'red' && <ExclamationCircleIcon className={`h-4 w-4 ${c.icon}`} />}
          <span className="text-sm font-semibold text-gray-900 dark:text-white">{title}</span>
          <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${c.badge}`}>
            {powers.length}
          </span>
        </div>
        <ChevronDownIcon className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`} />
      </button>
      {/* Collapsible power list with scroll constraint */}
      {!collapsed && (
        <div className="divide-y divide-gray-100 dark:divide-gray-700/50 bg-white dark:bg-gray-800 max-h-64 overflow-y-auto">
          {powers.map((p) => (
            <PowerRow
              key={p.powerNo}
              power={p}
              variant={variant}
              proofCount={(proofFilesByPower?.[p.powerNo] || []).length}
              onUploadProof={onUploadProof}
              onRemove={onRemove}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Component
// ============================================================================

const EMPTY_SET = new Set();

const DischargeModal = ({
  isOpen,
  onClose,
  power = null,
  selectedPowers = EMPTY_SET,
  selectAllMatching = false,
  totalMatchingCount = 0,
  onSuccess,
  onPowerRemoved,
  agencyId,
}) => {
  const mode = power ? 'single' : 'bulk';
  const powerNo = power?.powerNo;

  // Shared state
  const [step, setStep] = useState('validating'); // 'validating' | 'form' | 'confirm' | 'pending'
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [agentComments, setAgentComments] = useState('');
  const [isProofUploadOpen, setIsProofUploadOpen] = useState(false);

  // Per-power proof tracking: { [powerNo]: [fileId, ...] }
  const [proofFilesByPower, setProofFilesByPower] = useState({});

  // Single-mode: one validated power
  const [validatedPower, setValidatedPower] = useState(null);

  // Bulk-mode: categorized validation results
  const [bulkValidation, setBulkValidation] = useState(null); // { ready, needsProof, invalid }

  const count = useMemo(() => {
    if (mode === 'single') return 1;
    return selectAllMatching ? totalMatchingCount : selectedPowers.size;
  }, [mode, selectAllMatching, totalMatchingCount, selectedPowers.size]);

  // Power numbers to validate
  const powerNumbers = useMemo(() => {
    if (mode === 'single') return powerNo ? [powerNo] : [];
    return [...selectedPowers];
  }, [mode, powerNo, selectedPowers]);

  // Flattened proof file IDs for submission
  const allProofFileIds = useMemo(
    () => Object.values(proofFilesByPower).flat(),
    [proofFilesByPower],
  );

  const getProofCount = useCallback(
    (pNo) => (proofFilesByPower[pNo] || []).length,
    [proofFilesByPower],
  );

  // ---------------------------------------------------------------
  // Validate on open
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!isOpen || powerNumbers.length === 0) return;

    let cancelled = false;
    setStep('validating');
    setValidating(true);
    setError(null);
    setValidatedPower(null);
    setBulkValidation(null);

    const numbersToValidate = [...powerNumbers]; // snapshot

    (async () => {
      try {
        const result = await validatePowers(numbersToValidate, undefined, undefined, { agencyId });
        if (cancelled) return;

        if (result.success && result.data) {
          const validations = result.data.validations || [];
          const mapped = validations.map(mapValidationToDisplay);

          if (mode === 'single') {
            const match = mapped.find((v) => v.powerNo === powerNo);
            if (match) {
              setValidatedPower(match);
            } else {
              setError('Power was not returned by validation.');
            }
          } else {
            const categorized = categorizeValidations(validations);
            setBulkValidation({
              ready: categorized.ready.map(mapValidationToDisplay),
              needsProof: categorized.needsProof.map(mapValidationToDisplay),
              invalid: categorized.invalid.map(mapValidationToDisplay),
            });
          }
        } else {
          setError(result.error || 'Validation failed.');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Discharge validation error:', err);
          setError('An error occurred during validation.');
        }
      } finally {
        if (!cancelled) {
          setValidating(false);
          setStep('form');
        }
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-validate when the modal opens or the power changes; powerNumbers is read from a ref-stable memo
  }, [isOpen, powerNo]);

  // ---------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------

  // Single mode
  const isInvalid = mode === 'single' && validatedPower && !validatedPower.isValid;
  const needsProof = mode === 'single' && validatedPower?.requiresProof === true;

  // Bulk mode
  const bulkDischargeableCount = bulkValidation
    ? bulkValidation.ready.length + bulkValidation.needsProof.length
    : 0;
  const bulkTotalLiability = useMemo(() => {
    if (!bulkValidation) return 0;
    const sum = (arr) => arr.reduce((acc, p) => acc + (p.liability || 0), 0);
    return sum(bulkValidation.ready) + sum(bulkValidation.needsProof);
  }, [bulkValidation]);

  // Can proceed to review?
  const canReview = mode === 'single'
    ? (validatedPower?.isValid === true)
    : (bulkDischargeableCount > 0);

  // ---------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------
  const resetForm = useCallback(() => {
    setStep('validating');
    setValidating(false);
    setValidatedPower(null);
    setBulkValidation(null);
    setReason('');
    setAgentComments('');
    setSubmitting(false);
    setError(null);
    setProofFilesByPower({});
    setIsProofUploadOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetForm();
    onClose();
  }, [submitting, resetForm, onClose]);

  // ---------------------------------------------------------------
  // Proof handling (per-power)
  // ---------------------------------------------------------------
  const handleProofUpdated = useCallback((updatedPowerNo, fileId, _fileName, isDeleted = false) => {
    setProofFilesByPower((prev) => {
      const existing = prev[updatedPowerNo] || [];
      if (isDeleted) {
        const filtered = existing.filter((id) => id !== fileId);
        if (filtered.length === 0) {
          const { [updatedPowerNo]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [updatedPowerNo]: filtered };
      }
      return { ...prev, [updatedPowerNo]: [...existing, fileId] };
    });
  }, []);

  // ---------------------------------------------------------------
  // Remove a power from the bulk discharge list (in-modal cleanup
  // + propagate to parent so the table checkbox stays in sync)
  // ---------------------------------------------------------------
  const handleRemovePower = useCallback((removedPowerNo) => {
    if (!bulkValidation) return;
    const next = {
      ready: bulkValidation.ready.filter((p) => p.powerNo !== removedPowerNo),
      needsProof: bulkValidation.needsProof.filter((p) => p.powerNo !== removedPowerNo),
      invalid: bulkValidation.invalid.filter((p) => p.powerNo !== removedPowerNo),
    };
    setBulkValidation(next);
    setProofFilesByPower((prev) => {
      if (!prev[removedPowerNo]) return prev;
      const { [removedPowerNo]: _dropped, ...rest } = prev;
      return rest;
    });
    onPowerRemoved?.(removedPowerNo);
    const total = next.ready.length + next.needsProof.length + next.invalid.length;
    if (total === 0) {
      handleClose();
    }
  }, [bulkValidation, onPowerRemoved, handleClose]);

  // The power to pass to ProofUploadModal
  const [proofModalPower, setProofModalPower] = useState(null);

  const openProofUpload = useCallback((forPower) => {
    setProofModalPower(forPower);
    setIsProofUploadOpen(true);
  }, []);

  // Build the ProofUploadModal power object from a validated power (bulk row)
  const openProofUploadForValidated = useCallback((validatedP) => {
    openProofUpload({
      powerNo: validatedP.powerNo,
      bondAmount: validatedP.liability,
      defendantName: validatedP.defendant,
      proofReason: validatedP.reason,
    });
  }, [openProofUpload]);

  // ---------------------------------------------------------------
  // Review handler
  // ---------------------------------------------------------------
  const handleReview = () => {
    if (mode === 'single') {
      if (needsProof && getProofCount(powerNo) === 0) {
        setError('Proof of discharge is required. Please upload at least one document.');
        return;
      }
    }
    // Bulk: block if any needsProof power is missing proof
    if (mode === 'bulk' && bulkValidation?.needsProof?.length > 0) {
      const missingProof = bulkValidation.needsProof.filter((p) => getProofCount(p.powerNo) === 0);
      if (missingProof.length > 0) {
        setError(`${missingProof.length} power(s) require proof before discharge. Upload proof for all required powers.`);
        return;
      }
    }
    setError(null);
    setStep('confirm');
  };

  // ---------------------------------------------------------------
  // Submit handler (unified)
  // ---------------------------------------------------------------
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      // Collect dischargeable power numbers (skip invalid)
      let submitPowerNumbers;
      if (mode === 'single') {
        submitPowerNumbers = [powerNo];
      } else {
        submitPowerNumbers = [
          ...bulkValidation.ready.map((p) => p.powerNo),
          ...bulkValidation.needsProof.map((p) => p.powerNo),
        ];
      }

      const result = await submitDischarge({
        powerNumbers: submitPowerNumbers,
        agencyId,
        reason: reason || undefined,
        agentComments: agentComments || undefined,
        proofDocumentIds: allProofFileIds.length > 0 ? allProofFileIds : undefined,
      });

      if (result.success && result.data) {
        const msg = result.data.message
          || `${result.data.processedCount ?? submitPowerNumbers.length} power${submitPowerNumbers.length !== 1 ? 's' : ''} discharged successfully`;
        toast.success(msg);
        onSuccess?.();
        resetForm();
        onClose();
      } else {
        // If proof was already uploaded, the power is effectively pending review —
        // don't loop back to the form, show a pending state instead.
        if (allProofFileIds.length > 0) {
          setError(null);
          setStep('pending');
        } else {
          setError(result.error || 'Failed to discharge');
          setStep('form');
        }
      }
    } catch (err) {
      console.error('Discharge error:', err);
      if (allProofFileIds.length > 0) {
        setError(null);
        setStep('pending');
      } else {
        setError('An unexpected error occurred');
        setStep('form');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------
  // Title
  // ---------------------------------------------------------------
  const title = step === 'pending'
    ? 'Discharge Pending Review'
    : mode === 'single'
      ? `Discharge Power ${powerNo || ''}`
      : `Discharge ${count.toLocaleString()} Power${count !== 1 ? 's' : ''}`;

  // ---------------------------------------------------------------
  // Proof power for ProofUploadModal — with per-power existing file IDs
  // ---------------------------------------------------------------
  const proofPower = proofModalPower || (mode === 'single' ? {
    powerNo: validatedPower?.powerNo || powerNo,
    bondAmount: validatedPower?.liability,
    defendantName: validatedPower?.defendant,
    proofReason: validatedPower?.reason,
  } : null);

  const proofModalExistingIds = proofPower
    ? (proofFilesByPower[proofPower.powerNo] || [])
    : [];

  // ---------------------------------------------------------------
  // Footer
  // ---------------------------------------------------------------
  const handlePendingClose = useCallback(() => {
    toast.info('Proof uploaded — this power is pending review.');
    onSuccess?.();
    resetForm();
    onClose();
  }, [onSuccess, resetForm, onClose]);

  const footer = (
    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:justify-end">
      {step === 'confirm' && (
        <OathButton
          variant="outline"
          onClick={() => setStep('form')}
          disabled={submitting}
          className="w-full sm:w-auto"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back
        </OathButton>
      )}
      {step === 'pending' ? (
        <OathButton onClick={handlePendingClose} className="w-full sm:w-auto">
          Close
        </OathButton>
      ) : (
        <>
          <OathButton
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          {step === 'form' && canReview && !validating && (
            <OathButton
              onClick={handleReview}
              className="w-full sm:w-auto"
            >
              Review Discharge
            </OathButton>
          )}
          {step === 'confirm' && (
            <OathButton
              variant={mode === 'bulk' ? 'danger' : 'primary'}
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <OathSpinner size="sm" />
                  Processing...
                </span>
              ) : mode === 'single' ? (
                'Confirm Discharge'
              ) : (
                `Discharge ${bulkDischargeableCount} Power${bulkDischargeableCount !== 1 ? 's' : ''}`
              )}
            </OathButton>
          )}
        </>
      )}
    </div>
  );

  // ---------------------------------------------------------------
  // Single-mode proof button helper (avoids duplication)
  // ---------------------------------------------------------------
  const singleProofCount = getProofCount(powerNo);
  const singleHasProof = singleProofCount > 0;

  const renderSingleProofButton = (isRequired) => {
    const noProofClasses = isRequired
      ? 'bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400 dark:hover:bg-orange-900/30'
      : 'bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-gray-700/50 dark:text-gray-400 dark:hover:bg-gray-700';

    return (
      <button
        onClick={() => openProofUpload()}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
          singleHasProof
            ? 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30'
            : noProofClasses
        }`}
        title={singleHasProof ? 'Manage uploaded proof' : isRequired ? 'Upload proof' : 'Upload proof (optional)'}
      >
        {singleHasProof ? (
          <>
            <CheckCircleIcon className="h-4 w-4" />
            <span>Proof Uploaded</span>
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-300 text-xs font-bold">
              {singleProofCount}
            </span>
          </>
        ) : (
          <>
            <ArrowUpTrayIcon className="h-4 w-4" />
            <span>{isRequired ? 'Upload Proof' : 'Upload Proof (optional)'}</span>
          </>
        )}
      </button>
    );
  };

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  return (
    <OathModal
      isOpen={isOpen}
      onClose={submitting ? undefined : handleClose}
      title={title}
      size={mode === 'bulk' ? 'xl' : 'md'}
      footer={footer}
    >
      <div className="space-y-5">
        {error && (
          <OathAlert variant="error">
            {error}
          </OathAlert>
        )}

        {/* VALIDATING */}
        {validating && (
          <div className="flex flex-col items-center justify-center py-10">
            <OathSpinner size="lg" className="text-primary" />
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Validating {mode === 'single' ? 'power' : `${count} powers`}...
            </p>
          </div>
        )}

        {/* FORM STEP */}
        {step === 'form' && !validating && (
          <>
            {/* ---------- SINGLE MODE ---------- */}
            {mode === 'single' && validatedPower && (
              <>
                {/* Power info card */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 sm:p-5 space-y-2">
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-mono font-bold text-primary dark:text-primary-light text-base">
                      {validatedPower.powerNo}
                    </span>
                    <span className="text-base font-bold text-gray-900 dark:text-white tabular-nums">
                      {validatedPower.liability != null ? formatCurrencyWithSymbol(validatedPower.liability) : '—'}
                    </span>
                  </div>
                  {validatedPower.defendant && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">{validatedPower.defendant}</p>
                  )}
                </div>

                {/* Status + proof */}
                {isInvalid ? (
                  <OathAlert variant="error">
                    <div className="flex items-start gap-2">
                      <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Cannot discharge this power</p>
                        {validatedPower.validationErrors?.map((err, i) => (
                          <p key={i} className="text-sm opacity-90">{err}</p>
                        ))}
                      </div>
                    </div>
                  </OathAlert>
                ) : needsProof ? (
                  <div className="space-y-3">
                    <OathAlert variant="warning">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Proof of discharge required</p>
                        {validatedPower.reason && (
                          <p className="text-sm opacity-90">{validatedPower.reason}</p>
                        )}
                      </div>
                    </OathAlert>
                    {renderSingleProofButton(true)}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                        Ready to discharge
                      </p>
                    </div>
                    {renderSingleProofButton(false)}
                  </div>
                )}

                {/* Reason & comments */}
                {canReview && (
                  <div className="space-y-4 pt-1 border-t border-gray-200 dark:border-gray-700">
                    <OathInput
                      label="Reason for discharge"
                      value={reason}
                      onChange={setReason}
                      placeholder="e.g., Court dismissed, Charges dropped"
                    />
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Additional comments
                      </label>
                      <textarea
                        value={agentComments}
                        onChange={(e) => setAgentComments(e.target.value)}
                        placeholder="Any additional notes..."
                        rows={2}
                        className="w-full px-4 py-3 border rounded-xl text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 border-gray-300 dark:border-gray-600 resize-none"
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ---------- BULK MODE ---------- */}
            {mode === 'bulk' && bulkValidation && (
              <>
                <div className="space-y-3">
                  <SectionBlock
                    title="Ready to Discharge"
                    powers={bulkValidation.ready}
                    color="green"
                    variant="ready"
                    proofFilesByPower={proofFilesByPower}
                    onUploadProof={openProofUploadForValidated}
                    onRemove={handleRemovePower}
                  />
                  <SectionBlock
                    title="Needs Proof"
                    powers={bulkValidation.needsProof}
                    color="orange"
                    variant="needsProof"
                    proofFilesByPower={proofFilesByPower}
                    onUploadProof={openProofUploadForValidated}
                    onRemove={handleRemovePower}
                  />
                  <SectionBlock
                    title="Invalid / Cannot Discharge"
                    powers={bulkValidation.invalid}
                    color="red"
                    variant="invalid"
                    proofFilesByPower={proofFilesByPower}
                    onRemove={handleRemovePower}
                  />
                </div>

                {/* Summary bar */}
                {bulkDischargeableCount > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <span><span className="text-green-600 dark:text-green-400 font-semibold">{bulkValidation.ready.length}</span> ready</span>
                      {bulkValidation.needsProof.length > 0 && (
                        <span><span className="text-orange-600 dark:text-orange-400 font-semibold">{bulkValidation.needsProof.length}</span> pending review</span>
                      )}
                      {bulkValidation.invalid.length > 0 && (
                        <span><span className="text-red-600 dark:text-red-400 font-semibold">{bulkValidation.invalid.length}</span> invalid</span>
                      )}
                    </div>
                    {bulkTotalLiability > 0 && (
                      <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">
                        Total: {formatCurrencyWithSymbol(bulkTotalLiability)}
                      </span>
                    )}
                  </div>
                )}

                {/* Reason & comments */}
                {canReview && (
                  <div className="space-y-4 pt-1 border-t border-gray-200 dark:border-gray-700">
                    <OathInput
                      label="Reason for discharge"
                      value={reason}
                      onChange={setReason}
                      placeholder="e.g., Court dismissed, Charges dropped"
                    />
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Additional comments
                      </label>
                      <textarea
                        value={agentComments}
                        onChange={(e) => setAgentComments(e.target.value)}
                        placeholder="Any additional notes..."
                        rows={2}
                        className="w-full px-4 py-3 border rounded-xl text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 border-gray-300 dark:border-gray-600 resize-none"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* CONFIRM STEP */}
        {step === 'confirm' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg border bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800">
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
              <p className="text-sm text-orange-700 dark:text-orange-300">
                Please confirm the discharge details below.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {mode === 'single' && validatedPower ? (
                <>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Power Number</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{validatedPower.powerNo}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Liability</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                      {validatedPower.liability != null ? formatCurrencyWithSymbol(validatedPower.liability) : '—'}
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Defendant</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{validatedPower.defendant || '—'}</p>
                  </div>
                </>
              ) : bulkValidation && (
                <>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Powers to Discharge</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                      {bulkDischargeableCount} power{bulkDischargeableCount !== 1 ? 's' : ''}
                      {bulkValidation.invalid.length > 0 && (
                        <span className="text-red-500 dark:text-red-400 font-normal ml-1">
                          ({bulkValidation.invalid.length} excluded)
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Liability</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                      {formatCurrencyWithSymbol(bulkTotalLiability)}
                    </p>
                  </div>
                </>
              )}
              {allProofFileIds.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Proof Documents</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                    {allProofFileIds.length} file{allProofFileIds.length !== 1 ? 's' : ''}
                    {Object.keys(proofFilesByPower).length > 1 && (
                      <span className="font-normal text-gray-500 dark:text-gray-400">
                        {' '}across {Object.keys(proofFilesByPower).length} powers
                      </span>
                    )}
                  </p>
                </div>
              )}
              {reason && (
                <div className={allProofFileIds.length > 0 ? '' : 'sm:col-span-2'}>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reason</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{reason}</p>
                </div>
              )}
              {agentComments && (
                <div className="sm:col-span-2">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Comments</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{agentComments}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PENDING REVIEW STEP — proof uploaded, discharge pending */}
        {step === 'pending' && (
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center py-6 space-y-4">
              <div className="flex items-center justify-center h-14 w-14 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <ClockIcon className="h-7 w-7 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Pending Review
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 max-w-sm mx-auto">
                  Your proof of discharge has been uploaded and this power is now pending review.
                  It will be reviewed and discharged within 2 business days.
                </p>
              </div>
            </div>

            {/* Power summary */}
            {validatedPower && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono font-bold text-primary dark:text-primary-light text-sm">
                    {validatedPower.powerNo}
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                    {validatedPower.defendant || '—'}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
                    <CheckCircleIcon className="h-4 w-4" />
                    {allProofFileIds.length} proof{allProofFileIds.length !== 1 ? 's' : ''} uploaded
                  </span>
                  {validatedPower.liability != null && (
                    <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                      {formatCurrencyWithSymbol(validatedPower.liability)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Proof upload modal — tied to the specific power */}
      {isProofUploadOpen && proofPower && createPortal(
        <div className="relative z-[70]">
          <ProofUploadModal
            isOpen={isProofUploadOpen}
            onClose={() => setIsProofUploadOpen(false)}
            power={proofPower}
            existingFileIds={proofModalExistingIds}
            onProofUpdated={handleProofUpdated}
          />
        </div>,
        document.body
      )}
    </OathModal>
  );
};

export default DischargeModal;
